<script setup lang="ts">
definePage({
  name: 'AccessDenied',
  meta: {
    title: 'layout.accessDeniedTitle'
  }
});
</script>

<template>
  <svg class="svg-defs" xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
    <defs>
      <filter id="squiggly-0">
        <feTurbulence id="turbulence" baseFrequency="0.02" numOctaves="3" result="noise" seed="0" />
        <feDisplacementMap id="displacement" in="SourceGraphic" in2="noise" scale="2" />
      </filter>
      <filter id="squiggly-1">
        <feTurbulence id="turbulence" baseFrequency="0.02" numOctaves="3" result="noise" seed="1" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" />
      </filter>

      <filter id="squiggly-2">
        <feTurbulence id="turbulence" baseFrequency="0.02" numOctaves="3" result="noise" seed="2" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
      </filter>
      <filter id="squiggly-3">
        <feTurbulence id="turbulence" baseFrequency="0.02" numOctaves="3" result="noise" seed="3" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" />
      </filter>

      <filter id="squiggly-4">
        <feTurbulence id="turbulence" baseFrequency="0.02" numOctaves="3" result="noise" seed="4" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="1" />
      </filter>
    </defs>
  </svg>

  <section class="error">
    <div class="stage">
      <div class="key">
        <div class="key__head">
          <div class="key__eye"></div>
          <div class="key__eye"></div>
          <div class="key__mouth"></div>
        </div>
        <div class="key__body">
          <div class="key__indentation"></div>
          <div class="key__indentation"></div>
          <div class="key__indentation"></div>
          <div class="key__arm key__arm--left"></div>
          <div class="key__arm key__arm--right"></div>
        </div>
      </div>
      <div class="lock"></div>
    </div>
    <h1 class="error__header">403</h1>
    <h2 class="error__header error__header--secondary">{{ $t('layout.accessDeniedTitle') }}</h2>
    <p class="error__description">{{ $t('layout.accessDeniedText') }}</p>
    <p class="error__description">{{ $t('layout.accessDeniedHint') }}</p>
  </section>

</template>

<style scoped lang="scss">
$gold: #FDD835;
$goldDarker: darken($gold, 20);
$black: #111111;

$text: red;
$textSecondary: darkred;


html, body {
  font-family: Lato, Arial, serif;
  font-size: 120%;
  font-weight: 300;
  height: 100%;
}

.error {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 5rem 0;
  scale: 0.8;

  &__header {
    font-size: 5rem;
    font-weight: 900;
    padding: 1.5rem 0 0.5rem 0;

    &--secondary {
      font-size: 2.5rem;
      padding: 0 0 1rem 0;
      text-transform: uppercase;
    }
  }

  &__description {
    font-size: 1.2rem;
  }
}

.stage {
  height: 18rem;
  width: 8rem;
}


.key {
  animation-name: squigglevision;
  animation-duration: 0.35s;
  animation-direction: normal;
  animation-iteration-count: infinite;
  position: relative;
  height: 100%;
  width: 8rem;

  &__head {
    background: $gold;
    border-radius: 50%;
    box-shadow: 0.85rem 0 0 $goldDarker;
    height: 8rem;
    position: absolute;
    width: 8rem;
    z-index: 1;

    &::before {
      border-radius: 50%;
      box-shadow: inset 0.5rem 0.1rem 0 $goldDarker;
      content: "";
      display: block;
      height: 1rem;
      left: 3.5rem;
      position: absolute;
      width: 1rem;
      top: 1.5rem;
    }
  }

  &__eye {
    background-color: #111;
    border-radius: 50%;
    height: 0.75rem;
    left: 1.5rem;
    position: absolute;
    top: 4.5rem;
    width: 0.75rem;

    &:nth-child(2) {
      left: 5rem;
    }

    &::before {
      animation: scale 0.35s linear 0s infinite alternate;
      background: white;
      border-radius: 50%;
      content: "";
      display: block;
      height: 0.15rem;
      left: 0.15rem;
      position: absolute;
      width: 0.15rem;
      top: 0.15rem;
    }

    &::after {
      animation: scale 0.25s linear 0s infinite alternate;
      background: white;
      border-radius: 50%;
      content: "";
      display: block;
      height: 0.25rem;
      left: 0.35rem;
      position: absolute;
      width: 0.25rem;
      top: 0.35rem;
    }
  }

  &__mouth {
    animation: sniff 2s linear 0s infinite alternate;
    border-radius: 50%;
    border: 0.2rem solid $black;
    border-bottom-color: transparent;
    border-left-color: transparent;
    height: 1.25rem;
    left: 2.85rem;
    position: relative;
    top: 5rem;
    transform: rotate(-45deg);
    width: 1.25rem;
  }

  &__body {
    background: $gold;
    box-shadow: 1rem 0 0 $goldDarker;
    height: 9rem;
    position: relative;
    width: 3rem;
    top: 7rem;
    left: 2.5rem;

    &::before {
      background: transparentize($goldDarker, 0.5);
      border-radius: 1rem;
      content: "";
      display: block;
      height: 8.5rem;
      left: 1rem;
      position: absolute;
      top: 2rem;
      width: 0.45rem;
      z-index: 2;
    }

    &::after {
      background: $gold;
      border-radius: 0 0 80% 25%;
      box-shadow: 1rem 0 0 $goldDarker;
      content: "";
      display: block;
      height: 2rem;
      position: absolute;
      top: 100%;
      width: 3rem;
    }
  }

  &__arm {
    border-radius: 50%;
    border: 0.2rem solid $black;
    border-bottom-color: transparent;
    border-top-color: transparent;
    height: 4.55rem;
    position: absolute;
    width: 1.25rem;

    &--left {
      left: 1.9rem;
      top: 1rem;
      border-left-color: transparent;
    }

    &--right {
      left: -0.5rem;
      top: 1rem;
      border-right-color: transparent;
    }
  }

  &__indentation {
    background: $gold;
    box-shadow: 0.5rem -0.5rem 0 $goldDarker;
    height: 2rem;
    transform: rotate(45deg);
    width: 2rem;
    position: absolute;
    top: 2rem;
    left: 2rem;

    &:nth-child(2) {
      top: 4.5rem;
      width: 1.5rem;
      height: 1.5rem;
    }

    &:nth-child(3) {
      top: 6rem;
      width: 1.75rem;
      height: 1.75rem;
    }
  }

}

@keyframes scale {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.4);
  }
}

@keyframes sniff {
  0%, 70%, 100% {
    transform: translateY(0) rotate(-45deg);
  }
  75% {
    transform: translateY(-5%) rotate(-45deg);
  }
}

.svg-defs {
  display: none;
}

@keyframes squigglevision {
  0% {
    -webkit-filter: url('#squiggly-0');
    filter: url('#squiggly-0');
  }
  25% {
    -webkit-filter: url('#squiggly-1');
    filter: url('#squiggly-1');
  }
  50% {
    -webkit-filter: url('#squiggly-2');
    filter: url('#squiggly-2');
  }
  75% {
    -webkit-filter: url('#squiggly-3');
    filter: url('#squiggly-3');
  }
  100% {
    -webkit-filter: url('#squiggly-4');
    filter: url('#squiggly-4');
  }
}
</style>